import { cloneFabricJson } from '@/image-designer/utils/slideModel';
import { inlineUrlFromGoodsImageRef } from '@/pages/ipe/workbench/utils/goodsTemplateImageUrls';

/** 可替换贴合图层命名：ipelayer、ipelayer1、ipelayer2 … */
export const IPE_LAYER_NAME_PATTERN = /^ipelayer(\d*)$/i;

export const IPE_SLOT_ROLE = 'ipeSlot';

/**
 * @param {string} name
 * @returns {boolean}
 */
export function isIpeLayerName(name) {
  return Boolean(name && IPE_LAYER_NAME_PATTERN.test(String(name).trim()));
}

/**
 * 规范化为小写 ipelayer 键，便于 imageGroup 匹配
 * @param {string} name
 * @returns {string}
 */
export function normalizeIpeLayerName(name) {
  const trimmed = String(name || '').trim();
  const match = trimmed.match(IPE_LAYER_NAME_PATTERN);
  if (!match) return trimmed;
  const suffix = match[1];
  return suffix === '' || suffix === undefined ? 'ipelayer' : `ipelayer${suffix}`;
}

/**
 * ipelayer → 0，ipelayer1 → 1
 * @param {string} name
 * @returns {number}
 */
export function parseIpeLayerSlotIndex(name) {
  const normalized = normalizeIpeLayerName(name);
  if (normalized === 'ipelayer') return 0;
  const match = normalized.match(/^ipelayer(\d+)$/);
  return match ? Number(match[1]) : 0;
}

/**
 * @param {object} obj fabric JSON object
 * @returns {boolean}
 */
export function isIpeLayerObject(obj) {
  if (!obj || obj.id === 'workspace') return false;
  if (obj.type !== 'image') return false;
  if (obj.linkData?.role === IPE_SLOT_ROLE) return true;
  return isIpeLayerName(obj.name);
}

function getFabricObjectList(fabricJson) {
  return Array.isArray(fabricJson?.objects) ? fabricJson.objects : [];
}

function getWorkspaceFromFabricJson(fabricJson) {
  return getFabricObjectList(fabricJson).find((o) => o.id === 'workspace') || null;
}

/**
 * 对象在工作区内的像素边界
 * @param {object} obj
 * @param {object} workspace
 */
export function getIpeLayerBoundsInWorkspace(obj, workspace) {
  const wsLeft = workspace?.left || 0;
  const wsTop = workspace?.top || 0;
  const scaleX = obj.scaleX ?? 1;
  const scaleY = obj.scaleY ?? 1;
  const width = (obj.width || 0) * scaleX;
  const height = (obj.height || 0) * scaleY;
  return {
    x: (obj.left || 0) - wsLeft,
    y: (obj.top || 0) - wsTop,
    width,
    height,
    rotation: obj.angle || 0,
  };
}

/**
 * 扫描 fabric JSON 中所有 ipelayer 图层
 * @param {object} fabricJson
 * @returns {Array<{ name: string, slotIndex: number, object: object, bounds: object }>}
 */
export function findIpeLayerObjects(fabricJson) {
  const objects = getFabricObjectList(fabricJson);
  if (objects.length === 0) return [];
  const workspace = getWorkspaceFromFabricJson(fabricJson);
  return objects
    .filter(isIpeLayerObject)
    .map((obj) => {
      const name = normalizeIpeLayerName(obj.name || obj.linkData?.slotName || 'ipelayer');
      return {
        name,
        slotIndex: parseIpeLayerSlotIndex(name),
        object: obj,
        bounds: workspace
          ? getIpeLayerBoundsInWorkspace(obj, workspace)
          : { x: 0, y: 0, width: 0, height: 0, rotation: 0 },
      };
    })
    .sort((a, b) => a.slotIndex - b.slotIndex);
}

/**
 * @param {object} fabricJson
 * @returns {boolean}
 */
export function fabricJsonHasIpeLayers(fabricJson) {
  return findIpeLayerObjects(fabricJson).length > 0;
}

/**
 * 导出单页 ipeLayers 元数据
 * @param {object} fabricJson
 * @param {string} slideId
 */
export function exportIpeLayersFromFabricJson(fabricJson, slideId) {
  const layers = findIpeLayerObjects(fabricJson).map(({ name, slotIndex, bounds }) => ({
    name,
    slotIndex,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    rotation: bounds.rotation,
  }));
  return { [slideId]: layers };
}

/**
 * 导出全部 slide 的 ipeLayers
 * @param {Record<string, object>} slideCanvases
 */
export function exportIpeLayersFromAllSlides(slideCanvases = {}) {
  const result = {};
  Object.entries(slideCanvases).forEach(([slideId, fabricJson]) => {
    const exported = exportIpeLayersFromFabricJson(fabricJson, slideId);
    Object.assign(result, exported);
  });
  return result;
}

/**
 * 解析 slide 的贴合位：优先 fabric 扫描，其次缓存 ipeLayers，最后空数组
 * @param {string} slideId
 * @param {object} templateData
 * @returns {Array<{ name: string, slotIndex: number }>}
 */
export function getIpeLayersForSlide(slideId, templateData = {}) {
  const slideCanvas =
    templateData.slideCanvases?.[slideId]
    || (templateData.primaryImageId === slideId ? templateData.fabricCanvas : null);

  if (slideCanvas && fabricJsonHasIpeLayers(slideCanvas)) {
    return findIpeLayerObjects(slideCanvas).map(({ name, slotIndex }) => ({ name, slotIndex }));
  }

  const cached = templateData.ipeLayers?.[slideId];
  if (Array.isArray(cached) && cached.length > 0) {
    return cached.map((item, idx) => ({
      name: normalizeIpeLayerName(item.name || `ipelayer${idx + 1}`),
      slotIndex: item.slotIndex ?? parseIpeLayerSlotIndex(item.name),
    }));
  }

  return [];
}

/**
 * 判断该 slide 是否应使用 Fabric 全画布合成
 * （含 ipelayer 贴合位，或 stampArea 印花位）
 */
export function slideUsesFabricComposite(slideId, templateData = {}) {
  if (getIpeLayersForSlide(slideId, templateData).length > 0) return true;
  const slideCanvas =
    templateData.slideCanvases?.[slideId]
    || (templateData.primaryImageId === slideId ? templateData.fabricCanvas : null);
  const objects = Array.isArray(slideCanvas?.objects) ? slideCanvas.objects : [];
  return objects.some((obj) => (
    obj?.linkData?.role === 'stampArea' || obj?.stampRole === 'stampArea'
  ));
}

/**
 * 将 imageGroup（图层名 → 图片 id/url）应用到 fabric JSON，返回新 JSON
 * @param {object} fabricJson
 * @param {Record<string, string>} imageGroup
 * @param {Record<string, string>} imageUrlMap
 */
export function applyImagesToFabricJson(fabricJson, imageGroup = {}, imageUrlMap = {}) {
  const cloned = cloneFabricJson(fabricJson);
  if (!cloned.objects) return cloned;

  cloned.objects = cloned.objects.map((obj) => {
    if (!isIpeLayerObject(obj)) return obj;

    const layerName = normalizeIpeLayerName(obj.name);
    const rawRef =
      imageGroup[layerName]
      ?? imageGroup[obj.name]
      ?? imageGroup[`${layerName.charAt(0).toUpperCase()}${layerName.slice(1)}`];

    if (!rawRef) return obj;

    let url = null;
    const inline = inlineUrlFromGoodsImageRef(rawRef);
    if (inline) {
      url = inline;
    } else if (typeof rawRef === 'string' && rawRef.startsWith('data:image/')) {
      url = rawRef;
    } else if (typeof rawRef === 'string' && (rawRef.startsWith('http') || rawRef.startsWith('/'))) {
      url = rawRef;
    } else {
      const id = String(typeof rawRef === 'object'
        ? (rawRef.id || rawRef.imageId || rawRef.value || '')
        : rawRef).trim();
      url = imageUrlMap[id] || null;
    }

    if (!url) return obj;

    return {
      ...obj,
      src: url,
      linkData: {
        ...(obj.linkData || {}),
        role: IPE_SLOT_ROLE,
        slotName: layerName,
        replaced: true,
      },
    };
  });

  return cloned;
}

/**
 * 扩展 imageGroup：按 slotIndex 顺序将 ipelayer1→第1张上传图 等自动映射（缺省键时）
 * @param {Record<string, string>} imageGroup
 * @param {Array<{ name: string, slotIndex: number }>} ipeLayers
 * @param {string[]} orderedImageIds 按上传顺序的图片 id 列表
 */
export function expandImageGroupForIpeLayers(imageGroup, ipeLayers, orderedImageIds = []) {
  if (!ipeLayers?.length) return { ...imageGroup };
  const expanded = { ...imageGroup };
  const sorted = [...ipeLayers].sort((a, b) => a.slotIndex - b.slotIndex);
  const assignedCount = Object.values(expanded).filter((v) => v != null && v !== '').length;
  // 已按多印花位分配好时，不再用首张回填空位（避免一张贴满前后）
  const allowFallbackFill = assignedCount <= 1;

  sorted.forEach((layer, idx) => {
    if (expanded[layer.name]) return;
    if (!allowFallbackFill) return;
    const fallbackId = orderedImageIds[idx] ?? orderedImageIds[0];
    if (fallbackId) {
      expanded[layer.name] = fallbackId;
    }
  });

  return expanded;
}

/**
 * 收集 slide 所需的全部 ipelayer 名
 */
export function collectRequiredIpeLayerNames(ipeLayers) {
  return new Set((ipeLayers || []).map((l) => l.name));
}

/** 合成 loadFromJSON 前需签名的 fabric image.src（模板底图、ipelayer 等） */
export function collectFabricJsonImageSrcUrls(fabricJson) {
  if (!fabricJson?.objects) return [];
  return fabricJson.objects
    .filter((obj) => obj?.type === 'image')
    .map((obj) => String(obj.src || '').trim())
    .filter((src) => src && !src.startsWith('data:') && !src.startsWith('blob:'));
}
